import { useEffect, useMemo } from 'react'
import { v4 as uuid4 } from 'uuid'

import { PageNature } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { IActionType, TableAdapter } from '@makyoapp/makyo-common'

import { ViewComponent } from '@/core/app-builder/app/component'
import { toSdkPageBody } from '@/adapters/__project__/BuilderPageAdapter'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useFormFieldsGenerator } from '@/hooks/useFormFieldsGenerator'
import { useUpdateCompSettings } from '@/hooks/useUpdateCompSettings'
import { BuilderComponentSetting, ComponentSettingKey } from '@/shared/builder'
import { updateNestedBody } from '@/utils/builder/app-builder'
import { TableDataSourceDropdown } from '@/components/Builder/TableDataSourceDropdown'

interface TableSelectFieldSettingProps {
  id: string
  componentId: string
  settingId: string
  value: any
  findRelatedColSettings: (table: TableAdapter) => {
    id: string
    value: string | undefined
  }[]
  tableSelectSettings: BuilderComponentSetting['tableSelectSettings']
  findRelatedFilterSettings: () => {
    id: string
    value: {
      isAny: boolean
      conditions: never[]
    }
  }[]
  findUserFilterSettings: () => {
    id: string
    value: never[]
  }[]
}

const TableSelectFieldSetting = ({
  id,
  componentId,
  settingId,
  value,
  findRelatedColSettings,
  tableSelectSettings,
  findRelatedFilterSettings,
  findUserFilterSettings,
}: TableSelectFieldSettingProps) => {
  const { tables } = useDbClientContext()
  const { activeComponent, activeLayerContainer, activeLayerClient, isDetailPage } = useActiveLayerContext()
  const { generateFormFields } = useFormFieldsGenerator()
  const updateSettings = useUpdateCompSettings()

  const tableFound = !!tables.find((tb) => tb.id === value)

  const { detailScreenDependId, disabledWhenDetailScreen, includeSnapshot = false } = tableSelectSettings || {}

  const tableOptions = useMemo(() => {
    // only include snapshot if its already selected, mostly for detail-form page
    return includeSnapshot ? tables : tables.filter((tb) => !tb.isSnapshot || (tb.isSnapshot && value === tb.id))
  }, [tables, includeSnapshot, value])

  const shouldShow = activeLayerContainer?.tableId
    ? detailScreenDependId
      ? !Boolean(activeLayerContainer?.elementSettings[activeComponent?.id || '']?.[detailScreenDependId])
      : true
    : true

  const handleFormTableChange = (settings: Record<string, any>) => {
    // Early return if required contexts are missing
    if (!activeLayerClient?.settings || !activeLayerClient.componentsMap) return

    const component = activeLayerClient.componentsMap.get(componentId)
    if (!component) return

    // Generate new form fields first
    const { children, childElSettings } = generateFormFields(componentId, settings)

    let layerSettings = { ...activeLayerClient.settings._BodySettings }
    let layerBody = [...activeLayerClient.settings._Body]

    // Clean up old components and their settings
    const cleanupOldComponents = () => {
      for (const child of component._Body) {
        const childId = child._id
        delete layerSettings[childId]
        activeLayerClient.componentsMap.delete(childId)
      }
    }

    // Update with new components and settings
    const updateWithNewComponents = () => {
      // Update component body
      component._Body = toSdkPageBody(children)

      // Update settings
      layerSettings = {
        ...layerSettings,
        ...childElSettings,
      }

      // Update component map with new children
      for (const child of component._Body) {
        activeLayerClient.componentsMap.set(child._id, new ViewComponent(activeLayerClient, child, componentId))
      }

      // Update the body in page settings
      layerBody = updateNestedBody(layerBody, component.toBody())
    }

    // Execute updates
    cleanupOldComponents()
    updateWithNewComponents()

    // Notify client of changes
    activeLayerClient.UpdateComponent(component, {
      bodyArray: layerBody,
      bodySettings: layerSettings,
    })
  }

  const handleChangeTableSelect = (nextValue?: string) => {
    updateSettings((prevSettings) => {
      const oldValue = prevSettings[id]
      const isSame = oldValue === nextValue
      prevSettings[id] = nextValue

      // handle form datasource change
      if (id === ComponentSettingKey.FormDataSource) {
        prevSettings[ComponentSettingKey.FormActionSubmit] = [
          {
            id: uuid4(),
            type: IActionType.FormAddRow,
            settings: {
              tableId: nextValue,
              data: {},
            },
          },
        ]

        handleFormTableChange(prevSettings)
      } else if (!!nextValue && !isSame) {
        const table = tables.find((tb) => tb.id === nextValue)
        if (table) {
          const relatedSettings = findRelatedColSettings(table)
          for (const rs of relatedSettings) {
            const curValue = prevSettings[rs.id]
            const isArrayValue = Array.isArray(curValue)

            if (isArrayValue) {
              const arr = curValue as string[]
              const allValid = arr.length > 0 && arr.every((v) => table.columns.some((c) => c.id === v))
              if (!allValid) {
                prevSettings[rs.id] = rs.value ? [rs.value] : []
              }
            } else {
              const isEmpty = curValue === undefined || curValue === ''
              const isValid = !isEmpty && table.columns.some((c) => c.id === curValue)
              if (!isValid) {
                prevSettings[rs.id] = rs.value ?? ''
              }
            }
          }
          const relatedFilterSettings = findRelatedFilterSettings()
          for (const rfs of relatedFilterSettings) {
            prevSettings[rfs.id] = rfs.value
          }
          const userFilterSettings = findUserFilterSettings()
          for (const ufs of userFilterSettings) {
            prevSettings[ufs.id] = ufs.value
          }
        }
      }
    })
  }

  const disabled = disabledWhenDetailScreen && isDetailPage && activeLayerContainer.nature === PageNature.Form

  // make sure form datasource is the same as the detail page table, if its a form detail page
  useEffect(() => {
    if (!value || !activeLayerContainer?.tableId) return

    if (
      id === ComponentSettingKey.FormDataSource &&
      activeLayerContainer?.nature === PageNature.Form &&
      value !== activeLayerContainer.tableId
    ) {
      updateSettings((prevSettings) => {
        prevSettings[id] = activeLayerContainer.tableId
        return prevSettings
      })
    }
  }, [activeLayerContainer?.nature, activeLayerContainer.tableId, id, updateSettings, value])

  return !shouldShow ? null : (
    <TableDataSourceDropdown
      noSelect={tableFound}
      id={settingId}
      tables={tableOptions}
      value={value as string}
      onChange={handleChangeTableSelect}
      disabled={disabled}
    />
  )
}

export default TableSelectFieldSetting
